//@ts-nocheck

'use client'

import React from 'react'

import { useEffect, useRef } from 'react'
import MenuTab from './MenuTab/MenuTab.component'
import MenuContent from './MenuContent/MenuContent.component'

import * as S from './Menu.styles'

type Props = {
  data: any
  isOpen: boolean
  openSubmenu: string
  handleCloseMenu: () => void
  panelId?: string
  labelledById?: string
  scopeId?: string
}

const getFocusable = (root: HTMLElement) => {
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
    )
  ).filter(
    (el) => !el.hasAttribute('disabled') && !el.getAttribute('aria-hidden')
  )
}

const getTabbablesInScope = (scope?: HTMLElement | null) => {
  const root = scope ?? document.body
  return Array.from(
    root.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
    )
  ).filter(
    (el) =>
      !el.hasAttribute('disabled') &&
      !el.getAttribute('aria-hidden') &&
      !el.hidden
  )
}

const focusNextAfterTrigger = (triggerId?: string, scopeId?: string) => {
  const scopeEl = scopeId ? document.getElementById(scopeId) : null
  const tabbables = getTabbablesInScope(scopeEl)
  const trigger = triggerId
    ? (document.getElementById(triggerId) as HTMLElement | null)
    : null
  const idx = trigger ? tabbables.indexOf(trigger) : -1
  const next = idx >= 0 ? tabbables[idx + 1] : null
  next?.focus()
}

const focusPrevBeforeTrigger = (triggerId?: string, scopeId?: string) => {
  const scopeEl = scopeId ? document.getElementById(scopeId) : null
  const tabbables = getTabbablesInScope(scopeEl)
  const trigger = triggerId
    ? (document.getElementById(triggerId) as HTMLElement | null)
    : null
  const idx = trigger ? tabbables.indexOf(trigger) : -1
  const prev = idx > 0 ? tabbables[idx - 1] : null
  prev?.focus()
}

const Menu = ({
  data,
  isOpen,
  openSubmenu,
  handleCloseMenu,
  panelId,
  labelledById,
  scopeId,
}: Props) => {
  const menuRef = useRef(null)

  useEffect(() => {
    if (isOpen && menuRef.current) {
      const focusable = getFocusable(menuRef.current)
      if (focusable.length > 0) {
        // primo focusabile: una voce del menu
        setTimeout(() => {
          focusable[0].focus({ preventScroll: true })
        }, 500)
      }
    }
  }, [isOpen])

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        handleCloseMenu()
        // rimetti focus al trigger se esiste
        if (labelledById) {
          requestAnimationFrame(() => {
            const trigger = document.getElementById(
              labelledById
            ) as HTMLElement | null
            trigger?.focus()
          })
        }
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, labelledById])

  // Gestione TAB oltre l’ultimo / prima (sentinelle)
  const onFocusSentinelStart = () => {
    handleCloseMenu()
    // SHIFT+TAB sopra il primo → vai al precedente nell’header
    focusPrevBeforeTrigger(labelledById, scopeId)
  }
  const onFocusSentinelEnd = () => {
    handleCloseMenu()
    // TAB oltre l’ultimo → vai al prossimo nell’header
    focusNextAfterTrigger(labelledById, scopeId)
  }

  return (
    <S.Menu
      ref={menuRef}
      id={panelId}
      role="region"
      aria-labelledby={labelledById || undefined}
      aria-hidden={!isOpen}
      hidden={!isOpen}
    >
      {/* Sentinella inizio (prima del contenuto) */}
      <button
        type="button"
        tabIndex={isOpen ? 0 : -1}
        aria-hidden="true"
        onFocus={onFocusSentinelStart}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      />
      {data && data.length > 0 ? (
        <MenuContent
          data={data}
          isOpen={isOpen}
          submenu={openSubmenu}
          handleCloseMenu={handleCloseMenu}
        />
      ) : null}
      {/* Sentinella fine (dopo il contenuto) */}
      <button
        type="button"
        tabIndex={isOpen ? 0 : -1}
        aria-hidden="true"
        onFocus={onFocusSentinelEnd}
        style={{
          position: 'absolute',
          width: 1,
          height: 1,
          overflow: 'hidden',
          clip: 'rect(0 0 0 0)',
        }}
      />
    </S.Menu>
  )
}

export default Menu
